import {
  flip,
  offset,
  shift,
  useClientPoint,
  useFloating,
} from '@floating-ui/react';
import {
  AxisUnit,
  CreateChartOptions,
  EventsConfig,
} from '@toggle/chart';
import React, { RefObject, useMemo } from 'react';

import { useChartApi } from '~/hooks/use-chart-api/useChartApi';
import { useDomainChangeEventListener } from '~/hooks/use-domain-change-event-listener/useDomainChangeEventListener';
import { TooltipSortConfig } from '~/modules/multi-line-chart/MultiLineChartModule';
import { useGutterSize } from '~/modules/turbo-chart/hooks/use-gutter-size/useGutterSize';
import type { TerminalChartAssetData, TerminalPaneData } from '~/views/turbo-chart/utils/chart-utils/types';
import { Insights } from '~/views/turbo-chart/components/insights/Insights';
import { useChartInsights } from '~/views/turbo-chart/hooks/use-chart-insights/useChartInsights';
import { useLegendPriceHover } from '~/views/turbo-chart/hooks/use-legend-price-hover/useLegendPriceHover';
import { UseTurboChartReturn } from '~/views/turbo-chart/hooks/use-turbo-chart/useTurboChart';

import { SimpleChartTooltip } from '../simple-chart-tooltip/SimpleChartTooltip';

export interface ChartAreaProps {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  enablePriceHover?: boolean;
  options: CreateChartOptions;
  chartState: UseTurboChartReturn['chartState'];
  primaryAsset: TerminalChartAssetData;
  primaryPane: TerminalPaneData;
  onDomainChange?: (event: Event) => void;
  tooltipSortConfig?: TooltipSortConfig;
  axisUnit?: string;
  displayAxisUnit?: boolean;
  eventsConfig?: EventsConfig;
}

export const ChartArea = ({
  canvasRef,
  enablePriceHover = true,
  options,
  chartState,
  primaryAsset,
  primaryPane,
  onDomainChange,
  tooltipSortConfig,
  axisUnit,
  displayAxisUnit = true,
  eventsConfig = {
    TMI: false,
  },
}: ChartAreaProps) => {
  const chartApiRef = useChartApi({
    canvasRef,
    options,
    chartState,
    primaryAsset,
    primaryPane,
  });

  useDomainChangeEventListener({ canvasRef, onDomainChange });

  const { gutterSize } = useGutterSize({
    canvasRef,
  });

  const priceHover = useLegendPriceHover({
    elementRef: canvasRef,
    enabled: enablePriceHover,
  });

  const { refs, floatingStyles, context } = useFloating({
    open: !!priceHover,
    placement: 'top',
    strategy: 'fixed',
    middleware: [offset(15), flip(), shift({ padding: 5 })],
  });
  useClientPoint(context, {
    x: priceHover?.x,
    y: priceHover?.y,
  });

  const chartData = useMemo(
    () => chartState.chartPanes.flatMap(p => p.chartAssetData),
    [chartState.chartPanes]
  );
  const { insightsLoading, insights } = useChartInsights({
    chartData,
    eventsConfig,
  });

  const refCallback = (node: HTMLCanvasElement) => {
    canvasRef.current = node;
    refs.setReference(node);
  };

  const panes = gutterSize.panes;
  return (
    <>
      {context.open && (
        <SimpleChartTooltip
          ref={refs.setFloating}
          styles={floatingStyles}
          priceHover={priceHover}
          sortConfig={tooltipSortConfig}
        />
      )}
      <canvas
        data-testid="chart-area-canvas"
        ref={refCallback}
        style={{ width: options.width, height: options.height }}
      />
      {!!panes.length && axisUnit && displayAxisUnit && (
        <AxisUnit
          top={-10}
          label={axisUnit}
          isMixed={false}
          tooltipLabel=""
          offset={gutterSize.y - gutterSize.yAxisSizes[0].width}
          gutterWidth={gutterSize.yAxisSizes[0].width}
        />
      )}
      {!!insights.length && !insightsLoading && (
        <Insights
          chartApiRef={chartApiRef}
          canvasRef={canvasRef}
          insights={insights}
          eventsConfig={eventsConfig}
          isSimpleChart
        />
      )}
    </>
  );
};
